import React, { useState, useEffect, useCallback } from 'react'
import { Receipt, Plus, Search, Filter, Eye, Edit, Trash2, Calendar, Users, DollarSign, TrendingUp, ChevronDown, X, RefreshCw, Download } from 'lucide-react'
import toast from 'react-hot-toast'
import salaryService from '../../../services/salaryService'
import { employeeService } from '../../../services/employeeService'
import branchService from '../../../services/branchService'
import EditSalaryModal from '../../modals/EditSalaryModal'
import ViewSalaryModal from '../../modals/ViewSalaryModal'
import DeleteConfirmModal from '../../modals/DeleteConfirmModal'
import GeneratePayslipModal from '../../modals/GeneratePayslipModal'

const Salary = () => {
  const [salaries, setSalaries] = useState([])
  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const [selectedBranch, setSelectedBranch] = useState('')
  const [selectedMonth, setSelectedMonth] = useState('')
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [selectedSalary, setSelectedSalary] = useState(null)
  const [stats, setStats] = useState(null)

  const loadStats = useCallback(async () => {
    try {
      // Calculate stats from the current salary data
      if (salaries && salaries.length > 0) {
        const uniqueEmployees = new Set(salaries.map(salary => salary.employee?._id)).size
        const totalGross = salaries.reduce((sum, salary) => sum + (parseFloat(salary.grossSalary) || 0), 0)
        const totalNet = salaries.reduce((sum, salary) => sum + (parseFloat(salary.netSalary) || 0), 0)
        
        setStats({
          totalEmployees: uniqueEmployees,
          totalGross: totalGross,
          totalNet: totalNet,
          totalPayslips: salaries.length
        })
      } else {
        // Set default stats when no data
        setStats({
          totalEmployees: 0,
          totalGross: 0,
          totalNet: 0,
          totalPayslips: 0
        })
      }
    } catch (error) {
      console.error('Error calculating stats:', error)
      setStats({
        totalEmployees: 0,
        totalGross: 0,
        totalNet: 0,
        totalPayslips: 0
      })
    }
  }, [salaries])  // Depend on salaries data

  const loadSalaries = useCallback(async () => {
    try {
      setLoading(true)
      const filters = {}
      if (selectedEmployee) filters.employee = selectedEmployee
      if (selectedBranch) filters.branch = selectedBranch
      if (selectedMonth) filters.month = selectedMonth

      console.log('Loading salaries with filters:', filters)
      const response = await salaryService.getSalaries(filters)
      console.log('Raw salary response:', response) // Debug log
      
      // Handle different response structures
      let salaries = []
      if (response.data?.salaries) {
        salaries = response.data.salaries
      } else if (response.salaries) {
        salaries = response.salaries
      } else if (Array.isArray(response.data)) {
        salaries = response.data
      } else if (Array.isArray(response)) {
        salaries = response
      }
      
      console.log('Processed salaries:', salaries)
      setSalaries(salaries)
    } catch (error) {
      console.error('Error loading salaries:', error)
      toast.error(`Failed to load salaries: ${error.message || 'Unknown error'}`)
      setSalaries([]) // Set empty array on error
    } finally {
      setLoading(false)
    }
  }, [selectedEmployee, selectedBranch, selectedMonth])

  // Load initial data
  useEffect(() => {
    const initializeData = async () => {
      // Don't set a default month - show all months by default
      // setSelectedMonth(getCurrentMonth())
      
      // Load employees and branches first
      try {
        const [employeesResponse, branchesResponse] = await Promise.all([
          employeeService.getAllEmployees(),
          branchService.getAllBranches()
        ])
        setEmployees(employeesResponse.data.employees || [])
        setBranches(branchesResponse.data.branches || [])
      } catch (error) {
        console.error('Error loading initial data:', error)
      }
    }
    initializeData()
  }, [])

  // Reload salaries and stats when filters change
  useEffect(() => {
    // Load data regardless of filter values (including when "All Months" is selected)
    loadSalaries()
  }, [selectedEmployee, selectedBranch, selectedMonth, loadSalaries])

  // Update stats whenever salaries change
  useEffect(() => {
    loadStats()
  }, [salaries, loadStats])

  const handleGeneratePayslip = () => {
    setShowGenerateModal(true)
  }

  const handleViewSalary = (salary) => {
    setSelectedSalary(salary)
    setShowViewModal(true)
  }

  const handleEditSalary = (salary) => {
    setSelectedSalary(salary)
    setShowEditModal(true)
  }

  const handleDeleteSalary = (salary) => {
    setSelectedSalary(salary)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!selectedSalary) return
    
    try {
      await salaryService.deleteSalary(selectedSalary._id)
      toast.success('Salary deleted successfully')
      setShowDeleteModal(false)
      setSelectedSalary(null)
      loadSalaries()
      loadStats()
    } catch (error) {
      console.error('Error deleting salary:', error)
      toast.error(error.response?.data?.message || 'Failed to delete salary')
    }
  }

  const handleDownloadPDF = (salary) => {
    try {
      toast.loading('Generating PDF...', { id: 'pdf-download' })
      
      // Calculate totals
      const totalAllowances = Object.values(salary.allowances || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
      const totalDeductions = Object.values(salary.deductions || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
      
      // Get user and branch info
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const branch = JSON.parse(localStorage.getItem('selectedBranch') || '{}')
      
      // Create HTML for the payslip
      const payslipHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Payslip - ${salary.employee?.fullName}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              padding: 40px;
              background: white;
              color: #333;
            }
            .payslip-container { max-width: 800px; margin: 0 auto; }
            .header { 
              text-align: center; 
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 3px solid #f97316;
            }
            .company-name { 
              font-size: 28px; 
              font-weight: bold; 
              color: #f97316;
              margin-bottom: 5px;
            }
            .branch-name { 
              font-size: 16px; 
              color: #666;
              margin-bottom: 3px;
            }
            .document-title { 
              font-size: 20px; 
              font-weight: 600; 
              margin-top: 15px;
              color: #333;
            }
            .info-section { 
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 30px;
              padding: 20px;
              background: #f9fafb;
              border-radius: 8px;
            }
            .info-group { }
            .info-label { 
              font-size: 12px; 
              color: #666;
              text-transform: uppercase;
              margin-bottom: 4px;
              font-weight: 500;
            }
            .info-value { 
              font-size: 15px; 
              font-weight: 600;
              color: #333;
            }
            .salary-section { margin-bottom: 25px; }
            .section-title { 
              font-size: 16px; 
              font-weight: 600;
              margin-bottom: 12px;
              color: #333;
              padding-bottom: 8px;
              border-bottom: 2px solid #e5e7eb;
            }
            .salary-table { 
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
            }
            .salary-table td { 
              padding: 10px;
              border-bottom: 1px solid #e5e7eb;
            }
            .salary-table td:first-child { 
              color: #666;
              width: 60%;
            }
            .salary-table td:last-child { 
              text-align: right;
              font-weight: 600;
            }
            .total-row td { 
              font-weight: 700;
              font-size: 16px;
              padding-top: 15px;
              border-top: 2px solid #333;
              border-bottom: 2px solid #333;
            }
            .net-salary { 
              background: #f0fdf4;
              padding: 20px;
              border-radius: 8px;
              margin-top: 20px;
              text-align: center;
            }
            .net-salary-label { 
              font-size: 14px;
              color: #666;
              margin-bottom: 5px;
            }
            .net-salary-amount { 
              font-size: 32px;
              font-weight: bold;
              color: #16a34a;
            }
            .footer { 
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e5e7eb;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            .signature-section {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              margin-top: 50px;
              padding-top: 30px;
            }
            .signature-line {
              border-top: 2px solid #333;
              padding-top: 10px;
              text-align: center;
              font-weight: 600;
            }
            @media print {
              body { padding: 20px; }
              .payslip-container { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="payslip-container">
            <!-- Header -->
            <div class="header">
              <div class="company-name">TEZLAA CAFE</div>
              <div class="branch-name">${branch.name || 'Main Branch'}</div>
              <div class="branch-name">${branch.address || ''}</div>
              <div class="document-title">SALARY SLIP</div>
            </div>

            <!-- Employee Information -->
            <div class="info-section">
              <div class="info-group">
                <div class="info-label">Employee Name</div>
                <div class="info-value">${salary.employee?.fullName || 'N/A'}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Employee Type</div>
                <div class="info-value">${salary.employee?.employeeType || 'N/A'}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Pay Period</div>
                <div class="info-value">${formatDate(salary.month)}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Generated Date</div>
                <div class="info-value">${new Date(salary.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              </div>
            </div>

            <!-- Earnings -->
            <div class="salary-section">
              <div class="section-title">EARNINGS</div>
              <table class="salary-table">
                <tr>
                  <td>Basic Salary</td>
                  <td>${formatCurrency(salary.basicSalary || 0)}</td>
                </tr>
                ${Object.entries(salary.allowances || {}).map(([key, value]) => `
                  <tr>
                    <td>${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
                    <td>${formatCurrency(value)}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td>Total Earnings</td>
                  <td>${formatCurrency(salary.grossSalary || 0)}</td>
                </tr>
              </table>
            </div>

            <!-- Deductions -->
            <div class="salary-section">
              <div class="section-title">DEDUCTIONS</div>
              <table class="salary-table">
                ${Object.entries(salary.deductions || {}).map(([key, value]) => `
                  <tr>
                    <td>${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</td>
                    <td>${formatCurrency(value)}</td>
                  </tr>
                `).join('')}
                ${Object.keys(salary.deductions || {}).length === 0 ? '<tr><td colspan="2" style="text-align: center; color: #999;">No deductions</td></tr>' : ''}
                <tr class="total-row">
                  <td>Total Deductions</td>
                  <td>${formatCurrency(totalDeductions)}</td>
                </tr>
              </table>
            </div>

            <!-- Net Salary -->
            <div class="net-salary">
              <div class="net-salary-label">NET SALARY</div>
              <div class="net-salary-amount">${formatCurrency(salary.netSalary || 0)}</div>
            </div>

            <!-- Signature Section -->
            <div class="signature-section">
              <div>
                <div class="signature-line">Employee Signature</div>
              </div>
              <div>
                <div class="signature-line">Authorized Signature</div>
              </div>
            </div>

            <!-- Footer -->
            <div class="footer">
              <p>This is a computer-generated payslip and does not require a signature.</p>
              <p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </body>
        </html>
      `

      // Create a new window for printing
      const printWindow = window.open('', '_blank')
      printWindow.document.write(payslipHTML)
      printWindow.document.close()
      
      // Wait for content to load, then print
      setTimeout(() => {
        printWindow.focus()
        printWindow.print()
        toast.success('PDF generation dialog opened!', { id: 'pdf-download' })
      }, 500)
      
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Failed to generate PDF', { id: 'pdf-download' })
    }
  }

  const onPayslipGenerated = () => {
    loadSalaries()
    loadStats()
    setShowGenerateModal(false)
  }

  const onSalaryUpdated = () => {
    loadSalaries()
    loadStats()
    setShowEditModal(false)
    setSelectedSalary(null)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setSelectedEmployee('')
    setSelectedBranch('')
    setSelectedMonth('')
  }

  // Check if any filters are active
  const hasActiveFilters = searchTerm || selectedEmployee || selectedBranch || selectedMonth

  // Refresh data function
  const handleRefresh = async () => {
    toast.loading('Refreshing data...', { id: 'refresh' })
    try {
      await loadSalaries()
      toast.success('Data refreshed successfully!', { id: 'refresh' })
    } catch {
      toast.error('Failed to refresh data', { id: 'refresh' })
    }
  }

  // Format currency
  const formatCurrency = (amount) => {
    return `Rs ${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      })
    } catch {
      return 'Invalid date'
    }
  }

  // Filter salaries based on search term
  const filteredSalaries = salaries.filter(salary =>
    salary.employee?.fullName?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Generate month options
  const generateMonthOptions = () => {
    const options = []
    const currentDate = new Date()
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1)
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
      options.push({ value, label })
    }
    
    return options
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Employees</p>
                <p className="text-lg font-semibold text-gray-900">{stats.totalEmployees || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Gross</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(stats.totalGross || 0)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Net</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(stats.totalNet || 0)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Calendar className="h-5 w-5 text-orange-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Payslips</p>
                <p className="text-lg font-semibold text-gray-900">{stats.totalPayslips || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Salary Management</h2>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRefresh}
                className="bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg border border-gray-300 flex items-center space-x-2 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </button>
              <button
                onClick={handleGeneratePayslip}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Generate Payslip</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">Filter Targets</h3>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 transition-colors flex items-center space-x-2"
              >
               
                <span>Clear Filters</span>
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                />
              </div>
            </div>

            {/* Employee Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">All Types</label>
              <div className="relative">
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white appearance-none pr-10"
                >
                  <option value="">All Types</option>
                  {employees.map(employee => (
                    <option key={employee._id} value={employee._id}>
                      {employee.fullName}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
              </div>
            </div>

            {/* Branch Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">All Status</label>
              <div className="relative">
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white appearance-none pr-10"
                >
                  <option value="">All Status</option>
                  {branches.map(branch => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
              </div>
            </div>

            {/* Month Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select month</label>
              <div className="relative">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white appearance-none pr-10"
                >
                  <option value="">All Months</option>
                  {generateMonthOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Salary Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Month
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gross Salary
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Net Salary
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Allowances
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deductions
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSalaries.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">No salary records found</p>
                    <p className="text-sm text-gray-400">Generate payslips to get started</p>
                  </td>
                </tr>
              ) : (
                filteredSalaries.map((salary) => {
                  const totalAllowances = Object.values(salary.allowances || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
                  const totalDeductions = Object.values(salary.deductions || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
                  
                  return (
                    <tr key={salary._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(salary.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {salary.employee?.fullName || 'Unknown Employee'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {salary.employee?.employeeType || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(salary.month)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {formatCurrency(salary.grossSalary)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                        {formatCurrency(salary.netSalary)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 text-right">
                        {formatCurrency(totalAllowances)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 text-right">
                        {formatCurrency(totalDeductions)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleDownloadPDF(salary)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleViewSalary(salary)}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditSalary(salary)}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            title="Edit Salary"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteSalary(salary)}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            title="Delete Salary"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && selectedSalary && (
        <EditSalaryModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSalaryUpdated={onSalaryUpdated}
          salary={selectedSalary}
        />
      )}

      {showViewModal && selectedSalary && (
        <ViewSalaryModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          salary={selectedSalary}
        />
      )}

      {showDeleteModal && selectedSalary && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Salary Record"
          message={`Are you sure you want to delete the salary record for ${selectedSalary.employee?.fullName} (${formatDate(selectedSalary.month)})? This action cannot be undone.`}
        />
      )}

      {showGenerateModal && (
        <GeneratePayslipModal
          isOpen={showGenerateModal}
          onClose={() => setShowGenerateModal(false)}
          onPayslipGenerated={onPayslipGenerated}
        />
      )}
    </div>
  )
}

export default Salary
